import React, { createContext, useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";

import { IsUnrestrictedEraRoute } from "../constants/EraPaths";
import {
  removeLocalItem,
  getLocalItem,
  setLocalItem,
  getWithExpiry,
  setWithExpiry,
} from "shared/utils/localStorageAccess";
import { UserTypes } from "shared/constants/UserTypes";
import { ToSingleOrganization } from "shared/helpers/OrganizationHelpers";
import { FilterOptionsBySelectedRpids } from "shared/helpers/SelectedRpidFilter";
import {
  AreSameMmidLists,
  ToMmidKeyList,
} from "shared/helpers/SelectedLocationMemory";
import {
  ClaimUserSelectionCache,
  DECOMPOSE_GROUPS_KEY,
  SELECTED_LOCATIONS_KEY,
  SELECTED_RPIDS_KEY,
} from "shared/helpers/UserSelectionCache";

export const UserContext = createContext(null);

// MPI-415 cleanup: the retired multi-company header selector persisted its selection under this
// localStorage key. Every user is back on the single-select Organization dropdown, so the key is
// no longer read or written - it is cleared once on mount below so stale selections don't linger
// in existing users' browsers. Safe to delete this and the effect once rollout has settled.
const RETIRED_SELECTED_COMPANIES_KEY = "rme-selectedCompanies";

// DECOMPOSE_GROUPS_KEY backs the "Decompose Groups into Parent Companies" header option. Stored
// without an expiry, unlike the organization selection itself ("rme-userConfig", 1 hour), so the
// flag always outlives the selection it describes - the reverse would leave the dropdown showing
// parent companies the flag no longer accounts for.

// SELECTED_RPIDS_KEY holds the parent companies a GCT user has narrowed their selected Group down
// to. Expires on the same one hour schedule as the organization selection it refines
// ("rme-userConfig"), because the two are only meaningful together - a surviving RPID list
// against an expired Group selection would describe a narrowing of something the user is no
// longer looking at.

// SELECTED_LOCATIONS_KEY holds the member pharmacies (MMIDs) the user last picked on the shared
// NCPDP dropdown. Expires on the same one hour schedule as the organization selection, because
// MMIDs only mean anything against the organization they were chosen from.

const SELECTION_TTL_MS = 3600000;

// Shared empty lists so consumers that use selectedRpidsForGroup or selectedLocationMmids as an
// effect dependency are not re-run by a new array every render.
const EMPTY_RPIDS = [];
const EMPTY_MMIDS = [];

// Decomposing a Group into its parent companies is only offered to GCT users. A contact type is
// fixed at login and does not change during ordinary use, so this is effectively a per-user
// capability rather than something that varies while the app is running.
//
// UCT users were previously included, but a UCT user already sees every parent company as a
// top-level option in their own organization list and so has nothing to gain from decomposing a
// Group. PCT and MCT lists are built from GetMCTRecords and hold parent companies only, so they
// contain no Groups to decompose in the first place.
function CanDecomposeGroups(contactType) {
  return contactType === UserTypes.GCT;
}

function ToRpidList(input) {
  return Array.isArray(input) ? input : [];
}

export const UserContextWrapper = ({ children }) => {
  //a function to determine if the user is logged in or not

  // function isUserAuthenticated() {
  //   return oktaAuth.authStateManager.getAuthState().isAuthenticated;
  // }

  //const [isAuthenticated, setIsAuthenticated] = useState(isUserAuthenticated());

  const [programs, setPrograms] = useState(null);
  const [organizations, setOrganizations] = useState(null);
  // The single organization every screen scopes its data from.
  const [currentOrganization, setCurrentOrganization] = useState(null);
  // The parent companies a GCT user has narrowed the selected Group down to, held as RPID values.
  // Empty whenever the decompose option is off, which is every user except a GCT user who has
  // turned it on. It refines the selected Group rather than replacing it, so currentOrganization
  // stays a Group and nothing that reads it has to know this exists.
  const [selectedRpidsForGroup, setSelectedRpidsForGroup] = useState(() =>
    ToRpidList(getWithExpiry(SELECTED_RPIDS_KEY))
  );
  // The member pharmacies (MMIDs) the user last picked on the shared NCPDP dropdown. Restored on
  // mount so the filter survives both navigating between search pages and reloading the browser,
  // the same way the organization selection does. Empty means "no remembered selection", which
  // every dropdown reads as its usual select-all behaviour.
  const [selectedLocationMmids, setSelectedLocationMmids] = useState(() => {
    const restoredMmids = ToMmidKeyList(getWithExpiry(SELECTED_LOCATIONS_KEY));

    return restoredMmids.length > 0 ? restoredMmids : EMPTY_MMIDS;
  });
  const [locations, setLocations] = useState(null);
  const [recLocations, setRecLocations] = useState(null);
  const [metricLocations, setMetricLocations] = useState(null);
  const [ncpdps, setNcpdps] = useState(null);
  const [payers, setPayers] = useState(null);
  const [email, setEmail] = useState("");
  const [hasMetricSubscription, setHasMetricSubscription] = useState(null);
  const [hasRecSubscription, setHasRecSubscription] = useState(null);
  const [contactType, setContactType] = useState(null);
  const [isDecomposeGroupsEnabled, setIsDecomposeGroupsEnabled] = useState(
    () => getLocalItem(DECOMPOSE_GROUPS_KEY) === true
  );

  const [isUnrestrictedEraPath, setIsUnrestrictedEraPath] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(null);

  // const [constants, setConstants] = useState(null);

  const location = useLocation();

  const canDecomposeGroups = CanDecomposeGroups(contactType);
  // Forced off for anyone who is not allowed the option, so a flag left in localStorage by a GCT
  // session cannot change what a UCT, PCT or MCT user sees on the same browser.
  const isDecomposeEnabled = isDecomposeGroupsEnabled && canDecomposeGroups;

  // The refinement as everything outside this file sees it: the chosen parent companies for a GCT
  // user with the option on, and "the whole Group" for everyone else.
  const effectiveSelectedRpids = isDecomposeEnabled
    ? selectedRpidsForGroup
    : EMPTY_RPIDS;

  // The location lists are fetched for the selected Group as a whole, because the Group is still
  // what currentOrganization points at. Narrowing them here means every NCPDP / member pharmacy
  // picker in the app honours the refinement without a refetch and without any individual screen
  // having to know the option exists.
  //
  // Memoised because a lot of screens use these lists as a useEffect dependency - handing back a
  // new array each render would re-run their searches continuously. An empty selection returns
  // the list unchanged, so the reference survives for everyone who is not decomposing.
  //
  // null is left as null on purpose: consumers read it as "not loaded yet", which is not the same
  // as a loaded but empty list.
  const narrowedLocations = useMemo(
    () => FilterOptionsBySelectedRpids(locations, effectiveSelectedRpids),
    [locations, effectiveSelectedRpids]
  );

  const narrowedRecLocations = useMemo(
    () => FilterOptionsBySelectedRpids(recLocations, effectiveSelectedRpids),
    [recLocations, effectiveSelectedRpids]
  );

  const narrowedMetricLocations = useMemo(
    () => FilterOptionsBySelectedRpids(metricLocations, effectiveSelectedRpids),
    [metricLocations, effectiveSelectedRpids]
  );

  const narrowedNcpdps = useMemo(
    () => FilterOptionsBySelectedRpids(ncpdps, effectiveSelectedRpids),
    [ncpdps, effectiveSelectedRpids]
  );

  let userConfig = {
    programs: programs,
    onProgramsChange: (programs) => setPrograms(programs),
    organizations: organizations,
    onOrganizationsChange: (organizations) => setOrganizations(organizations),
    currentOrganization: currentOrganization,
    // Tolerates a list so a selection cached by the short-lived multi-select build still loads;
    // its first entry wins.
    onCurrentOrganizationChange: (organization) =>
      setCurrentOrganization(ToSingleOrganization(organization)),
    // Only ever populated for a GCT user with the decompose option on. Treat an empty list as
    // "the whole Group", which is what every other contact type always gets.
    selectedRpidsForGroup: effectiveSelectedRpids,
    onSelectedRpidsForGroupChange: (rpids) => {
      const updatedRpids = ToRpidList(rpids);

      setWithExpiry(SELECTED_RPIDS_KEY, updatedRpids, SELECTION_TTL_MS);
      setSelectedRpidsForGroup(updatedRpids);
    },
    // The member pharmacies last picked on the shared NCPDP dropdown. Empty means "nothing
    // remembered", which every dropdown reads as its usual select-all behaviour.
    selectedLocationMmids: selectedLocationMmids,
    onSelectedLocationMmidsChange: (mmids) => {
      const updatedMmids = ToMmidKeyList(mmids);

      // Every dropdown reports its selection on mount, so an unchanged list arrives routinely.
      // Bailing out keeps that from re-rendering every consumer and re-triggering the dropdown
      // effects that produced it.
      if (AreSameMmidLists(updatedMmids, selectedLocationMmids)) return;

      setWithExpiry(SELECTED_LOCATIONS_KEY, updatedMmids, SELECTION_TTL_MS);
      setSelectedLocationMmids(
        updatedMmids.length > 0 ? updatedMmids : EMPTY_MMIDS
      );
    },
    // The setters store what the server returned for the whole Group; the values read back here
    // are narrowed to selectedRpidsForGroup. Keeping the raw lists in state means turning the
    // decompose option off, or changing the refinement, restores the full Group without a refetch.
    locations: narrowedLocations,
    onLocationsChange: (locations) => {
      //alert("locations changed");
      setLocations(locations);
    },
    recLocations: narrowedRecLocations,
    onRecLocationsChange: (recLocations) => setRecLocations(recLocations),
    metricLocations: narrowedMetricLocations,
    onMetricLocationsChange: (metricLocations) =>
      setMetricLocations(metricLocations),
    //NOTE TODO: Are we actually using the "ncpdps" value anywhere? Or is it just "locations" that we're using? check this
    ncpdps: narrowedNcpdps,
    onNcpdpsChange: (ncpdps) => setNcpdps(ncpdps),
    // Payers are deliberately not narrowed: the GetManualRemittancePayers payload carries no RPID,
    // so there is nothing to match a parent company on without a server-side change.
    payers: payers,
    onPayersChange: (payers) => setPayers(payers),
    email: email,
    onEmailChange: (authenticatedEmail) => {
      if (email == authenticatedEmail) return;

      //alert("email changed" + authenticatedEmail);

      // A different user signing in must not inherit the previous user's selection, whether it
      // is still held in memory or only persisted in the browser.
      const isUserSwitch = !!email;
      const wasCacheDiscarded = ClaimUserSelectionCache(authenticatedEmail);

      if (isUserSwitch || wasCacheDiscarded) {
        setCurrentOrganization(null);
        setSelectedRpidsForGroup(EMPTY_RPIDS);
        setSelectedLocationMmids(EMPTY_MMIDS);
        setIsDecomposeGroupsEnabled(false);
      }

      if (isUserSwitch) {
        setPrograms(null);
        setOrganizations(null);
        setLocations(null);
        setRecLocations(null);
        setMetricLocations(null);
        setNcpdps(null);
        setPayers(null);
        setHasMetricSubscription(null);
        setHasRecSubscription(null);
        setContactType(null);
      }

      setIsAuthenticated(true);
      setEmail(authenticatedEmail);
    },
    hasMetricSubscription: hasMetricSubscription,
    onHasMetricSubscriptionChange: (hasMetricSubscription) =>
      setHasMetricSubscription(hasMetricSubscription),
    hasRecSubscription: hasRecSubscription,
    onHasRecSubscriptionChange: (hasRecSubscription) =>
      setHasRecSubscription(hasRecSubscription),
    contactType: contactType,
    onContactTypeChange: (contactType) => setContactType(contactType),
    canDecomposeGroups: canDecomposeGroups,
    isDecomposeGroupsEnabled: isDecomposeEnabled,
    onDecomposeGroupsEnabledChange: (isEnabled) => {
      setLocalItem(DECOMPOSE_GROUPS_KEY, isEnabled === true);
      setIsDecomposeGroupsEnabled(isEnabled === true);
    },
    isUnrestrictedEraPath: isUnrestrictedEraPath,
    isAuthenticated: isAuthenticated,
  };

  // MPI-415 cleanup: drop any multi-company selection left behind in localStorage by the
  // retired header selector. Runs once per mount; harmless when the key is already absent.
  useEffect(() => {
    removeLocalItem(RETIRED_SELECTED_COMPANIES_KEY);
  }, []);

  useEffect(() => {
    let isCurrentRouteUnrestricted = IsUnrestrictedEraRoute(location.pathname);

    //set the state if the the reswtricted/unrestricted state of the current route
    if (isCurrentRouteUnrestricted !== isUnrestrictedEraPath) {
      setIsUnrestrictedEraPath(isCurrentRouteUnrestricted);
    }
  }, [location.pathname]);

  return (
    <UserContext.Provider value={userConfig}>{children}</UserContext.Provider>
  );
};
